import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Newspaper, Sparkles, Plus, Pencil, Trash2, Loader2, Save, X, Eye, EyeOff, ExternalLink, Clock, PlayCircle, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'retail', label: 'Retail Tech' },
  { key: 'ia', label: 'IA & Automatización' },
  { key: 'esl', label: 'ESL & Signage' },
  { key: 'logistica', label: 'Logística' },
  { key: 'sustentabilidad', label: 'Sustentabilidad' },
  { key: 'innovacion', label: 'Innovación' },
];

const catColor = (c) => ({
  retail: 'bg-sky-100 text-sky-700',
  ia: 'bg-teal-100 text-teal-700',
  esl: 'bg-amber-100 text-amber-700',
  logistica: 'bg-violet-100 text-violet-700',
  sustentabilidad: 'bg-emerald-100 text-emerald-700',
  innovacion: 'bg-orange-100 text-orange-700',
}[c] || 'bg-slate-100 text-slate-700');

const empty = { title: '', summary: '', category: 'retail', source: '', source_url: '', image_url: '', kian_take: '', active: true };

export default function KianNewsAdmin({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null); // null = none, {} = new, {...item} = edit
  const [saving, setSaving] = useState(false);
  const [cronLogs, setCronLogs] = useState([]);
  const [runningCron, setRunningCron] = useState(false);
  const client = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/admin/news');
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Error al cargar noticias');
    }
    setLoading(false);
    // Also refresh cron logs
    try {
      const { data: logData } = await client.get('/admin/cron/log?limit=5');
      setCronLogs(logData.logs || []);
    } catch (e) { /* non-critical */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const runCronNow = async () => {
    if (!window.confirm('Ejecutar el cron de Kian ahora mismo? (Reemplazará las noticias de hoy)')) return;
    setRunningCron(true);
    const tId = toast.loading('Kian está trabajando...');
    try {
      await client.post('/admin/cron/run-now');
      toast.success('Cron ejecutado con éxito', { id: tId });
      await load();
    } catch (e) {
      toast.error(`Error: ${e?.response?.data?.detail || e.message}`, { id: tId });
    }
    setRunningCron(false);
  };

  const generateWithKian = async () => {
    if (!window.confirm('¿Generar 6 noticias con Kian (Claude Sonnet 4.5)? Toma ~30 segundos.')) return;
    setGenerating(true);
    const tId = toast.loading('Kian está curando las noticias del día...');
    try {
      const { data } = await client.post('/admin/news/generate-kian', { count: 6, replace_today: false });
      toast.success(`Kian generó ${data.count} noticias nuevas`, { id: tId });
      await load();
    } catch (e) {
      toast.error(`Error generando: ${e?.response?.data?.detail || e.message}`, { id: tId });
    }
    setGenerating(false);
  };

  const save = async (form) => {
    setSaving(true);
    try {
      if (form.id) {
        await client.put(`/admin/news/${form.id}`, form);
        toast.success('Noticia actualizada');
      } else {
        await client.post('/admin/news', form);
        toast.success('Noticia creada');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(`Error: ${e?.response?.data?.detail || e.message}`);
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar noticia? Esta acción no se puede deshacer.')) return;
    try {
      await client.delete(`/admin/news/${id}`);
      toast.success('Noticia eliminada');
      await load();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const toggleActive = async (it) => {
    try {
      await client.put(`/admin/news/${it.id}`, { active: !it.active });
      await load();
    } catch (e) { toast.error('Error'); }
  };

  return (
    <div data-testid="admin-kian-news">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2540] flex items-center gap-2" style={{ fontFamily: 'Syne,sans-serif' }}>
            <Newspaper className="w-6 h-6" /> Kian Daily · Noticias
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestiona las noticias curadas que Kian publica cada día.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generateWithKian}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0A2540] text-[#F8D90F] text-sm font-bold hover:bg-[#050E1F] disabled:opacity-50 shadow-lg"
            data-testid="admin-news-generate-kian"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? 'Kian está trabajando...' : 'Generar con Kian'}
          </button>
          <button
            onClick={() => setEditing(empty)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0EA5E9] text-white text-sm font-semibold hover:bg-sky-600 shadow"
            data-testid="admin-news-new"
          >
            <Plus className="w-4 h-4" />Nueva
          </button>
        </div>
      </div>

      {/* CRON STATUS BANNER */}
      <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#050E1F] text-white border border-white/10" data-testid="admin-kian-cron-banner">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F8D90F]/10 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-[#F8D90F]" />
            </div>
            <div>
              <div className="text-xs font-bold text-[#F8D90F] uppercase tracking-wider mb-0.5">Automatización activa</div>
              <div className="text-sm font-semibold">Kian genera 6 noticias cada día a las 07:00 (hora Chile)</div>
              {cronLogs.length > 0 && (
                <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1.5">
                  Última ejecución:
                  {cronLogs[0].status === 'success' ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <AlertCircle className="w-3 h-3 text-red-400" />}
                  <span>{new Date(cronLogs[0].run_at).toLocaleString('es-CL')}</span>
                  {cronLogs[0].count !== undefined && <span className="text-emerald-400">· {cronLogs[0].count} noticias</span>}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={runCronNow}
            disabled={runningCron}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold border border-white/20 transition-all disabled:opacity-50"
            data-testid="admin-cron-run-now"
          >
            {runningCron ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Ejecutar ahora
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9] mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 rounded-2xl bg-white border border-slate-200">
          <Newspaper className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500 mb-4">Aún no hay noticias. Genera las 6 del día con Kian o crea manualmente.</p>
          <button onClick={generateWithKian} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0A2540] text-[#F8D90F] text-sm font-bold"><Sparkles className="w-4 h-4" />Generar con Kian</button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Título</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Categoría</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Fecha</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Fuente</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Estado</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-slate-50" data-testid={`admin-news-row-${it.id}`}>
                  <td className="px-4 py-3 max-w-md">
                    <div className="font-medium text-[#0A2540] line-clamp-2">{it.title}</div>
                    {it.generated_by && <div className="text-[10px] text-slate-400 mt-0.5">✨ {it.generated_by}</div>}
                  </td>
                  <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${catColor(it.category)}`}>{it.category}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{it.published_at ? new Date(it.published_at).toLocaleDateString('es-CL') : '-'}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{it.source || '-'}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(it)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${it.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {it.active ? <><Eye className="w-3 h-3" />Activa</> : <><EyeOff className="w-3 h-3" />Oculta</>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {it.source_url && it.source_url !== '#' && <a href={it.source_url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-slate-100 rounded" title="Ver fuente"><ExternalLink className="w-4 h-4 text-slate-500" /></a>}
                      <button onClick={() => setEditing(it)} className="p-1.5 hover:bg-sky-50 rounded" title="Editar" data-testid={`admin-news-edit-${it.id}`}><Pencil className="w-4 h-4 text-[#0EA5E9]" /></button>
                      <button onClick={() => remove(it.id)} className="p-1.5 hover:bg-red-50 rounded" title="Eliminar" data-testid={`admin-news-delete-${it.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <EditModal item={editing} onSave={save} onCancel={() => setEditing(null)} saving={saving} />}
    </div>
  );
}

function EditModal({ item, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ ...empty, ...item });
  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e) => { e.preventDefault(); onSave(form); };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-bold text-[#0A2540]">{form.id ? 'Editar noticia' : 'Nueva noticia'}</h3>
            <button type="button" onClick={onCancel} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
          </div>
          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase">Título *</label>
              <input required value={form.title} onChange={e => change('title', e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase">Resumen *</label>
              <textarea required value={form.summary} onChange={e => change('summary', e.target.value)} rows={3} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase">Kian opina</label>
              <textarea value={form.kian_take || ''} onChange={e => change('kian_take', e.target.value)} rows={2} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Opinión estratégica de Kian (opcional)" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase">Categoría</label>
                <select value={form.category} onChange={e => change('category', e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                  {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase">Fuente</label>
                <input value={form.source || ''} onChange={e => change('source', e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Retail Dive" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase">URL de la fuente</label>
              <input type="url" value={form.source_url || ''} onChange={e => change('source_url', e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="https://..." />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase">URL de la imagen</label>
              <input type="url" value={form.image_url || ''} onChange={e => change('image_url', e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="https://images.unsplash.com/..." />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.active !== false} onChange={e => change('active', e.target.checked)} className="w-4 h-4 rounded" />
              Publicada (visible en el sitio)
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200">Cancelar</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0EA5E9] text-white text-sm font-semibold hover:bg-sky-600 disabled:opacity-50" data-testid="admin-news-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
